import React, { createContext, useContext, useState, useCallback } from "react";

export interface IpfsUrls {
  cover: string | null;
  icon: string | null;
  nft: string | null;
  content?: string;
  nftMimeType?: string;
}

interface FormData {
  [key: string]: string | boolean;
}

interface CollectionContextType {
  ipfsUrls: IpfsUrls;
  setIpfsUrls: (urls: IpfsUrls) => void;
  formData: FormData;
  setFormData: (data: FormData) => void;
  initialize: () => void;
  collectionIcon: string | null;
  setCollectionIcon: (icon: string | null) => void;
}

const CollectionContext = createContext<CollectionContextType | undefined>(
  undefined
);

export const CollectionProvider = ({ children }) => {
  const [ipfsUrls, setIpfsUrls] = useState<IpfsUrls>({
    cover: null,
    icon: null,
    nft: null,
    content: undefined,
    nftMimeType: undefined,
  });
  const [formData, setFormData] = useState({});
  const [collectionIcon, setCollectionIcon] = useState<string | null>(null);

  const initialize = useCallback(() => {
    setIpfsUrls({
      cover: null,
      icon: null,
      nft: null,
      content: undefined,
      nftMimeType: undefined,
    });
    setFormData({});
    setCollectionIcon(null);
  }, []);

  const updateIpfsUrls = useCallback(urls => {
    setIpfsUrls(urls);
  }, []);

  const updateFormData = useCallback(data => {
    setFormData(prevData => ({
      ...prevData,
      ...data,
    }));
  }, []);

  const value = {
    ipfsUrls,
    formData,
    setIpfsUrls: updateIpfsUrls,
    setFormData: updateFormData,
    initialize,
    collectionIcon,
    setCollectionIcon,
  };

  return (
    <CollectionContext.Provider value={value}>
      {children}
    </CollectionContext.Provider>
  );
};

export function useCollection() {
  const context = useContext(CollectionContext);
  if (undefined === context) {
    throw new Error("useCollection must be used within a CollectionProvider");
  }
  return context;
}
